import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Users, Search, Plus, ChevronLeft, ChevronRight, AlertTriangle, Filter, X, Check, ChevronDown,
  Upload, Download, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { apiFetch, downloadFile } from '@/lib/api';
import { track } from '@/lib/analytics';
import { formatPhone } from '@/lib/phone';
import { useT } from '@/lib/i18n';
import { ConversationDrawer } from '@/components/ConversationDrawer';
import { ContactDrawer } from '@/components/contacts/ContactDrawer';
import { ContactFormDialog } from '@/components/contacts/ContactFormDialog';
import { ImportContactsDialog } from '@/components/contacts/ImportContactsDialog';
import {
  type Contact, type Taxon, type ContactStatus,
  ALL_STATUSES, DELIVERY_ISSUE_STATUSES, contactIdent, statusVariant,
} from '@/lib/contacts';

const PAGE_SIZE = 25;

function relTime(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Multi-seleção (tags/grupos) num dropdown com checkboxes.
function MultiSelect({
  label, options, selected, onChange,
}: {
  label: string;
  options: Taxon[];
  selected: string[];
  onChange: (keys: string[]) => void;
}) {
  const toggle = (k: string) =>
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-9 items-center gap-1.5 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm outline-none transition-colors hover:bg-accent">
        {label}
        {selected.length > 0 && <span className="rounded-full bg-primary/15 px-1.5 text-xs font-semibold text-primary">{selected.length}</span>}
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 min-w-[13rem] overflow-y-auto p-1">
        {options.length === 0 ? (
          <p className="px-2 py-2 text-sm text-muted-foreground">—</p>
        ) : options.map((o) => {
          const on = selected.includes(o.key);
          return (
            <button
              key={o.id}
              onClick={() => toggle(o.key)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent"
            >
              <span className={'flex size-4 shrink-0 items-center justify-center rounded border ' + (on ? 'border-primary bg-primary text-primary-foreground' : 'border-input')}>
                {on && <Check className="size-3" />}
              </span>
              <span className="flex-1 truncate">{o.name}</span>
              {typeof o.count === 'number' && <span className="text-xs text-muted-foreground">{o.count}</span>}
            </button>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ContactsResponse { data: Contact[]; total: number; limit: number; offset: number }

export function Contacts() {
  const t = useT();
  const qc = useQueryClient();

  // Filtros
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContactStatus | 'all' | 'issues'>('all');
  const [tags, setTags] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [project, setProject] = useState('all');
  const [instance, setInstance] = useState('all');
  const [page, setPage] = useState(0);

  // Painéis
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [convo, setConvo] = useState<Contact | null>(null);

  const { data: tagList = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/tags')).data as { data?: Taxon[] })?.data ?? [],
  });
  const { data: groupList = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<Taxon[]> => ((await apiFetch('GET', '/contact-groups')).data as { data?: Taxon[] })?.data ?? [],
  });
  const { data: projectList = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: async (): Promise<{ id: string; name: string }[]> => ((await apiFetch('GET', '/projects')).data as { data?: { id: string; name: string }[] })?.data ?? [],
  });
  // Números seguem o projeto selecionado: 'all' → todos da conta; um id → aquele projeto.
  const { data: numberList = [] } = useQuery({
    queryKey: ['instances', project],
    queryFn: async (): Promise<{ id: string; phone: string; nickname?: string }[]> => ((await apiFetch('GET', `/instances?project_id=${encodeURIComponent(project)}`)).data as { data?: { id: string; phone: string; nickname?: string }[] })?.data ?? [],
  });

  // Filtros da tela, sem paginação: a listagem acrescenta limit/offset e a
  // exportação usa exatamente isto — o que você vê é o que você exporta.
  const filters = useMemo(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('search', search.trim());
    if (status === 'issues') p.set('status', DELIVERY_ISSUE_STATUSES.join(','));
    else if (status !== 'all') p.set('status', status);
    if (tags.length) p.set('tags', tags.join(','));
    if (groups.length) p.set('groups', groups.join(','));
    if (city.trim()) p.set('city', city.trim());
    if (state.trim()) p.set('state', state.trim());
    if (project !== 'all') p.set('project_id', project);
    if (instance !== 'all') p.set('instance_id', instance);
    p.set('sort', 'last_activity');
    return p;
  }, [search, status, tags, groups, city, state, project, instance]);

  const query = useMemo(() => {
    const p = new URLSearchParams(filters);
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(page * PAGE_SIZE));
    return p.toString();
  }, [filters, page]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['contacts', query],
    queryFn: async (): Promise<ContactsResponse> => {
      const r = await apiFetch('GET', `/contacts?${query}`);
      const d = r.data as Partial<ContactsResponse> | undefined;
      return { data: d?.data ?? [], total: d?.total ?? 0, limit: d?.limit ?? PAGE_SIZE, offset: d?.offset ?? 0 };
    },
  });

  const list = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = !!(search || status !== 'all' || tags.length || groups.length || city || state || project !== 'all' || instance !== 'all');

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(0); };
  const refresh = () => qc.invalidateQueries({ queryKey: ['contacts'] });

  const clearFilters = () => {
    setSearch(''); setStatus('all'); setTags([]); setGroups([]); setCity(''); setState(''); setProject('all'); setInstance('all'); setPage(0);
  };
  // Trocar de projeto reseta o número (a lista de números depende do projeto).
  const changeProject = (v: string) => { setProject(v); setInstance('all'); setPage(0); };

  /** Exporta o CSV com os MESMOS filtros da tela (o nome do arquivo é o do servidor). */
  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile(`/contacts/export?${filters.toString()}`, 'contacts.csv');
      track('contacts_exported', { filtered: hasFilters });
    } catch {
      toast.error(t('contacts.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('contacts.subtitle')}</p>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" /> {t('contacts.import')}
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={exporting}>
            {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} {t('contacts.export')}
          </Button>
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="size-4" /> {t('contacts.newContact')}
          </Button>
        </div>
      </div>

      {/* Barra de filtros */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[14rem] flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => resetPage(setSearch)(e.target.value)}
                placeholder={t('contacts.search')}
                className="pl-8"
              />
            </div>

            <div className="w-40">
              <Select value={status} onValueChange={(v) => resetPage<ContactStatus | 'all' | 'issues'>(setStatus)(v as ContactStatus | 'all' | 'issues')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('contacts.filterAllStatus')}</SelectItem>
                  {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`contacts.status.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <MultiSelect label={t('contacts.tags')} options={tagList} selected={tags} onChange={resetPage(setTags)} />
            <MultiSelect label={t('contacts.groups')} options={groupList} selected={groups} onChange={resetPage(setGroups)} />

            <Input value={city} onChange={(e) => resetPage(setCity)(e.target.value)} placeholder={t('contacts.city')} className="w-32" />
            <Input value={state} onChange={(e) => resetPage(setState)(e.target.value)} placeholder={t('contacts.state')} className="w-24" />

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="size-4" /> {t('common.clear')}
              </Button>
            )}
          </div>

          {/* Projeto + número numa linha própria (o número depende do projeto) */}
          {(projectList.length > 1 || numberList.length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              {projectList.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{t('contacts.filterProject')}:</span>
                  <div className="w-52">
                    <Select value={project} onValueChange={changeProject}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('contacts.filterAllProjects')}</SelectItem>
                        {projectList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {numberList.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">{t('contacts.filterNumber')}:</span>
                  <div className="w-52">
                    <Select value={instance} onValueChange={(v) => resetPage(setInstance)(v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('contacts.filterAllNumbers')}</SelectItem>
                        {numberList.map((n) => <SelectItem key={n.id} value={n.id}>{n.nickname || formatPhone(n.phone)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Atalho: problemas de recebimento */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Filter className="size-3.5" /> {t('contacts.quickFilters')}:</span>
            <button
              onClick={() => resetPage<ContactStatus | 'all' | 'issues'>(setStatus)(status === 'issues' ? 'all' : 'issues')}
              className={'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ' +
                (status === 'issues' ? 'border-destructive bg-destructive/12 text-destructive' : 'border-input text-muted-foreground hover:bg-accent')}
            >
              <AlertTriangle className="size-3.5" /> {t('contacts.deliveryIssues')}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="space-y-3 p-5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <Users className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{hasFilters ? t('contacts.noResults') : t('contacts.empty')}</p>
              {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters}>{t('common.clear')}</Button>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-5 py-3 font-medium">{t('contacts.contact')}</th>
                  <th className="hidden px-5 py-3 font-medium md:table-cell">{t('contacts.statusCol')}</th>
                  <th className="hidden px-5 py-3 font-medium lg:table-cell">{t('contacts.tags')}</th>
                  <th className="hidden px-5 py-3 font-medium sm:table-cell">{t('contacts.messages')}</th>
                  <th className="px-5 py-3 text-right font-medium">{t('contacts.lastMessage')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => {
                  const phone = contactIdent(c, formatPhone);
                  return (
                    <tr key={c.id} onClick={() => setDetailId(c.id)} className="cursor-pointer border-b last:border-0 transition-colors hover:bg-muted/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                            {c.avatar_url
                              ? <img src={c.avatar_url} alt="" className="size-full object-cover" />
                              : (c.name || phone || '?').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{c.name || phone}</p>
                            {c.name && phone && <p className="truncate text-xs text-muted-foreground">{phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-5 py-3 md:table-cell">
                        <Badge variant={statusVariant(c.status)}>{t(`contacts.status.${c.status}`)}</Badge>
                      </td>
                      <td className="hidden px-5 py-3 lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags ?? []).slice(0, 3).map((tg) => (
                            <span key={tg} className="rounded-full bg-accent px-2 py-0.5 text-xs text-muted-foreground">{tg}</span>
                          ))}
                          {(c.tags?.length ?? 0) > 3 && <span className="text-xs text-muted-foreground">+{(c.tags!.length - 3)}</span>}
                        </div>
                      </td>
                      <td className="hidden px-5 py-3 text-muted-foreground sm:table-cell">{c.message_count}</td>
                      <td className="px-5 py-3 text-right text-xs text-muted-foreground">{relTime(c.last_message_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{t('contacts.showing', { from: page * PAGE_SIZE + 1, to: Math.min(total, (page + 1) * PAGE_SIZE), total })}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0 || isFetching} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <ChevronLeft className="size-4" />
            </Button>
            <span>{page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages || isFetching} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      <ContactFormDialog open={newOpen} onOpenChange={setNewOpen} onCreated={refresh} />

      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} onImported={refresh} />

      <ContactDrawer
        contactId={detailId}
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onMutated={refresh}
        onEditProfile={() => {
          const c = list.find((x) => x.id === detailId);
          if (c) { setConvo(c); setDetailId(null); }
        }}
      />

      <ConversationDrawer
        open={!!convo}
        onOpenChange={(o) => { if (!o) setConvo(null); }}
        chatJid={convo?.chat_jid ?? null}
        name={convo?.name}
        avatarUrl={convo?.avatar_url}
      />
    </div>
  );
}
